import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  trackEvent,
  getVisitorId,
  getSessionId,
  getStoredUTM,
  getStoredClickIds,
} from "@/lib/tracking";

// Single-field email capture. Fires `email_signup` (mapped to Meta `Lead`)
// AND POSTs to /api/subscribe so the email lands in the durable
// `subscribers` Supabase table (owned-audience asset). The two paths are
// independent — analytics still works if /api/subscribe fails, and the
// subscribers row still lands if the Pixel is blocked.
async function sha256Hex(s) {
  if (!s || typeof crypto === "undefined" || !crypto.subtle) return null;
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s.trim().toLowerCase()));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
}

export default function EmailCapture({ titleId, slug, prompt, success }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/.+@.+\..+/.test(email)) { setError("That doesn't look like an email."); return; }
    setError("");
    const email_hash = await sha256Hex(email);
    trackEvent("email_signup", { title_id: titleId, slug, email_hash });
    setSubmitted(true);

    // Persist to Supabase via Worker. Fire-and-forget: the UI flip already
    // happened, and `email_signup` event fired through /api/events/web
    // independently — so even if this network call fails we don't lose
    // the analytics signal, only the durable subscriber row.
    try {
      const utm = getStoredUTM();
      const clickIds = getStoredClickIds();
      const payload = {
        email,
        source_slug: slug,
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        utm_content: utm.utm_content,
        utm_term: utm.utm_term,
        fbclid: clickIds.fbclid,
        gclid: clickIds.gclid,
        page_url: typeof window !== "undefined" ? window.location.href : undefined,
      };
      fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  };

  return (
    <div className="max-w-xl">
      <p className="font-serif text-lg md:text-xl text-ink-soft leading-relaxed">
        {prompt || "Be the first to know when the next volume arrives."}
      </p>
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5"
          >
            <p className="font-serif italic text-dharma-gold-deep text-lg">
              {success || "Thank you. We'll write when there's something worth reading."}
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={handleSubmit}
            className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent border-b border-dharma-gold/40 text-ink py-3 px-1 font-serif text-lg focus:outline-none focus:border-dharma-gold transition-colors"
            />
            <button type="submit" className="btn-pill btn-pill-solid whitespace-nowrap">
              Notify me
              <ArrowRight size={14} />
            </button>
          </motion.form>
        )}
      </AnimatePresence>
      {error && <p className="font-body text-xs text-saffron-fire mt-2">{error}</p>}
    </div>
  );
}
